const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const roundRgb = ({ r, g, b }) => ({
  r: clamp(Math.round(r), 0, 255),
  g: clamp(Math.round(g), 0, 255),
  b: clamp(Math.round(b), 0, 255),
});

export function rgbToCss(rgb) {
  const safe = roundRgb(rgb);
  return `rgb(${safe.r}, ${safe.g}, ${safe.b})`;
}

export function rgbaToCss(rgb, alpha) {
  const safe = roundRgb(rgb);
  return `rgba(${safe.r}, ${safe.g}, ${safe.b}, ${alpha})`;
}

function rgbToHsl({ r, g, b }) {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: lightness };
  }

  const delta = max - min;
  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue;

  switch (max) {
    case red:
      hue = (green - blue) / delta + (green < blue ? 6 : 0);
      break;
    case green:
      hue = (blue - red) / delta + 2;
      break;
    default:
      hue = (red - green) / delta + 4;
      break;
  }

  return { h: hue / 6, s: saturation, l: lightness };
}

function hslToRgb({ h, s, l }) {
  if (s === 0) {
    const value = l * 255;
    return roundRgb({ r: value, g: value, b: value });
  }

  const hueToRgb = (p, q, rawT) => {
    let t = rawT;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return roundRgb({
    r: hueToRgb(p, q, h + 1 / 3) * 255,
    g: hueToRgb(p, q, h) * 255,
    b: hueToRgb(p, q, h - 1 / 3) * 255,
  });
}

function normalizeHueForUi({ h, s }) {
  if (s < 0.08) return h;
  if (h >= 0.56 && h <= 0.7) return 0.5;
  return h;
}

function paletteColor(sourceHsl, lightness, saturationMultiplier = 1) {
  const sourceSaturation = Number(sourceHsl.s) || 0;
  const saturation =
    sourceSaturation <= 0.02
      ? 0
      : clamp(Math.max(sourceSaturation, 0.18) * saturationMultiplier, 0.16, 0.68);

  return hslToRgb({
    h: sourceHsl.h,
    s: saturation,
    l: lightness,
  });
}

export function buildImageGradientPalette(sourceRgb, fallbackRgb) {
  const source = sourceRgb || fallbackRgb;
  const sourceHsl = rgbToHsl(roundRgb(source));
  const isNeutralSource = sourceHsl.s < 0.08;
  const hsl = {
    ...sourceHsl,
    h: normalizeHueForUi(sourceHsl),
    s: isNeutralSource ? 0 : clamp(sourceHsl.s, 0.18, 0.68),
  };
  const saturationBoost = isNeutralSource ? 1 : sourceHsl.s < 0.18 ? 1.32 : 1.06;
  const baseLightness = clamp(sourceHsl.l * 0.58, 0.2, 0.34);

  const soft = paletteColor(hsl, clamp(baseLightness + 0.1, 0.28, 0.42), saturationBoost * 0.9);
  const base = paletteColor(hsl, baseLightness, saturationBoost);
  const floor = paletteColor(hsl, clamp(baseLightness - 0.06, 0.1, 0.24), saturationBoost * 1.04);
  const shadow = paletteColor(hsl, clamp(baseLightness - 0.1, 0.08, 0.18), saturationBoost);

  return {
    source: roundRgb(source),
    soft,
    base,
    floor,
    shadow,
    softCss: rgbToCss(soft),
    baseCss: rgbToCss(base),
    floorCss: rgbToCss(floor),
    shadowCss: rgbToCss(shadow),
  };
}

export function averageImageDataColor(data) {
  let red = 0;
  let green = 0;
  let blue = 0;
  let totalWeight = 0;

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3] / 255;
    if (alpha < 0.78) continue;

    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const luminance = (max + min) / 510;
    const saturation = max === 0 ? 0 : (max - min) / max;
    const usableLight = 1 - Math.abs(luminance - 0.46);
    let weight = alpha * (0.45 + saturation * 1.45) * (0.72 + usableLight * 0.5);

    if (max < 20) weight *= 0.35;
    if (min > 236) weight *= 0.25;

    red += r * weight;
    green += g * weight;
    blue += b * weight;
    totalWeight += weight;
  }

  if (totalWeight <= 0) return null;

  return roundRgb({
    r: red / totalWeight,
    g: green / totalWeight,
    b: blue / totalWeight,
  });
}
